import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Divider,
  useTheme,
  Dialog,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStores } from "../../../../use-stores";
import uploadicon from "../../../../assets/uploadimg.png";
import playBtn from "../../../../assets/playbtn.png";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import imageIcon from "../../../../assets/image.svg";
import videoIcon from "../../../../assets/video.svg";
import AppButton from "../../../components/common/buttons/AppButton";
import ImageUploadDialog from "../../../components/common/upload/ImageUploadDialog";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../components/common/form/CustomTypography";
import { useNotification } from "../../../../utils/NotificationsContext";
import ImageAPI from "../../../../api/ImageAPI";
import VideoAPI from "../../../../api/VideoAPI";
import ImageGalleryPopup from "../../../components/common/pop-up/ImageGalleryPopup";

interface MediaItem {
  id: number;
  label: string;
  image_url?: string;
  video_url?: string;
}

const PackagingOpsMedia = () => {
  const theme = useTheme();
  const { orderStore, projectPackagingOpsStore } = useStores();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openUploadPopup, setOpenUploadPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<"image" | "video">("image");
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<MediaItem | null>(null);
  const [videoPopupOpen, setVideoPopupOpen] = useState(false);
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const projectId = Number(searchParams.get("project-id"));
  const inventoryId = Number(searchParams.get("inventory-id"));
  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    const hasVideo = searchParams.has("video");
    if (hasVideo) {
      setActiveTab("video");
    } else {
      setActiveTab("image");
    }
  }, [searchParams]);

  useEffect(() => {
    getPackagingOpsSwagpackImageVideo(projectId, inventoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, inventoryId]);

  const getPackagingOpsSwagpackImageVideo = (
    projectId: number,
    inventoryId: number,
  ) => {
    if (inventoryId) {
      setIsLoading(true);
      projectPackagingOpsStore
        .getPackagingOpsSwagpackImageVideo(projectId, inventoryId)
        .catch((error) => {
          showError(
            "Error occurred",
            error?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        })
        .finally(() => setIsLoading(false));
    }
  };

  const allMedia = (projectPackagingOpsStore.packagingOpsSwagpackMedia?.media ||
    []) as MediaItem[];

  const allImages = allMedia.filter(
    (item) => item.image_url !== null,
  ) as MediaItem[];
  const allVideos = allMedia.filter(
    (item) => item.video_url !== null,
  ) as MediaItem[];

  const handleTabChange = (tab: "image" | "video") => {
    setActiveTab(tab);
    navigate(
      `/projects/packaging-ops-media?${tab}&project-id=${projectId}&inventory-id=${inventoryId}`,
    );
  };

  // const handleImageNavigation = (direction: "next" | "prev") => {
  //   if (!selectedImage) return;

  //   const currentIndex = allImages.findIndex(
  //     (img) => selectedImage && img.image_url === selectedImage.image_url,
  //   );
  //   if (currentIndex === -1) return;

  //   let newIndex =
  //     direction === "next"
  //       ? (currentIndex + 1) % allImages.length
  //       : (currentIndex - 1 + allImages.length) % allImages.length;

  //   setSelectedImage(allImages[newIndex]);
  // };

  type MediaType = "image" | "video";

  const uploadMedia = async (
    files: File[],
    type: MediaType,
    categoryOrContainer: string,
  ): Promise<string[]> => {
    if (files.length === 0) return [];
    if (type === "image") {
      const imageAPI = new ImageAPI();
      return imageAPI.uploadImage(
        files,
        categoryOrContainer || "product-images",
      );
    } else if (type === "video") {
      const videoAPI = new VideoAPI();
      return videoAPI.uploadVideo(files, categoryOrContainer);
    }
    return [];
  };

  const handleUpload = async (files: File[]) => {
    const isImage = activeTab === "image";
    const type = isImage ? "image" : "video";
    if (!inventoryId) {
      showError("Error", "Please create or map inventory first");
      return;
    }

    setIsUploading(true);

    if (!inventoryId) {
      showError("Error", "Please create or map inventory first");
      return;
    }

    setIsUploading(true);
    uploadMedia(files, type, "order-videos")
      .then((uploadedUrls) => {
        if (uploadedUrls.length > 0 && inventoryId) {
          return projectPackagingOpsStore.uploadPackagingImageVideo(
            projectId,
            inventoryId,
            uploadedUrls,
            isImage,
          );
        }
        return Promise.resolve();
      })
      .then(() => {
        showSuccess(`${isImage ? "Images" : "Videos"} uploaded successfully.`);
        if (inventoryId) {
          return getPackagingOpsSwagpackImageVideo(projectId, inventoryId);
        }
        return Promise.resolve();
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload media";
        showError("Upload Error", errorMessage);
      })
      .finally(() => {
        setIsUploading(false);
        setOpenUploadPopup(false);
      });
  };
  const handleDeleteMedia = async (mediaId: number, isImage: boolean) => {
    setIsLoading(true);
    projectPackagingOpsStore
      .deletePackagingMedia(projectId, inventoryId, mediaId)
      .then(() => {
        showSuccess(`${isImage ? "Images" : "Videos"} Deleted successfully.`);
      })
      .catch((error) => {
        showError(
          "Error",
          error?.response?.data?.message || "Failed to delete media",
        );
      })
      .finally(() => setIsLoading(false));
  };
  return (
    <>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={isLoading || isUploading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container
        maxWidth="xl"
        sx={{
          pl: { xl: "6.25rem", lg: "4.063rem" },
          pr: { xl: "6.25rem", lg: "4.063rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            color: theme.palette.customColors.greyTextColor,
            mb: 2,
            mt: 2,
            maxWidth: "3.75rem",
            "&:hover, &:active": {
              color: theme.palette.customColors.greyTextColor,
            },
          }}
          onClick={() => {
            navigate(`/projects/${projectId}?tab_index=3`);
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Back</span>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 5,
            }}
          >
            <Box>
              <CustomerHeading
                label={orderStore.orderBasicDetails?.label || ""}
                orderId={projectId}
                moduleName="Packaging Media Gallery"
                brandName={
                  projectPackagingOpsStore.packagingRequestBody.companyName ||
                  ""
                }
              />
            </Box>
            <Box
              sx={{
                paddingBottom: "1.5em",
                color: theme.palette.customColors.greenDark,
              }}
            >
              <Box display="flex" gap={2}>
                <Box>
                  <AppButton
                    label="Photos"
                    onClick={() => handleTabChange("image")}
                    customStyles={{
                      height: "2.563rem",
                      width: "8.438rem",

                      backgroundColor:
                        activeTab === "image"
                          ? theme.palette.customColors.darkBlue
                          : theme.palette.customColors.white,
                      color:
                        activeTab === "image"
                          ? theme.palette.customColors.white
                          : theme.palette.text.primary,
                    }}
                    startIcon={imageIcon}
                    variant={activeTab === "image" ? "primary" : "grayscale"}
                  />
                </Box>
                <Box>
                  <AppButton
                    label="Videos"
                    onClick={() => handleTabChange("video")}
                    customStyles={{
                      height: "2.563rem",
                      width: "8.438rem",
                      backgroundColor:
                        activeTab === "video"
                          ? theme.palette.customColors.darkBlue
                          : theme.palette.customColors.white,
                      color:
                        activeTab === "video"
                          ? theme.palette.customColors.white
                          : theme.palette.text.primary,
                    }}
                    startIcon={videoIcon}
                    variant={activeTab === "video" ? "primary" : "grayscale"}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mt: 2, mb: 3 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              justifyContent: "flex-end",
              flexWrap: "wrap",
              width: "100%",
              paddingBottom: "2rem",
            }}
          >
            <AppButton
              label={`Upload ${activeTab === "image" ? "Image" : "Video"}`}
              onClick={() => setOpenUploadPopup(true)}
              customStyles={{
                height: "2.563rem",
                minWidth: "11.438rem",
                backgroundColor: theme.palette.customColors.white,
                border: `1px solid ${theme.palette.customColors.purple}`,
                color: theme.palette.customColors.purple,
              }}
              startIcon={uploadicon}
              variant="primary"
              disabled={
                Number(
                  projectPackagingOpsStore.packagingOpsSwagpackMedia?.archived,
                ) === 1 || !inventoryId
              }
            />
          </Box>

          <ImageUploadDialog
            open={openUploadPopup}
            onClose={() => setOpenUploadPopup(false)}
            onUpload={handleUpload}
            multiple={true}
            heading={`Upload ${activeTab === "image" ? "Image" : "Video"}`}
            uploadsubText={
              activeTab === "image"
                ? "Drop your image here"
                : "Drop your video here"
            }
            accept={activeTab === "image" ? "image/*" : "video/*"}
          />

          <Box>
            {activeTab === "image" && (
              <Box>
                {allImages.length > 0 ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(175px, 1fr))",
                      columnGap: 3,
                      rowGap: 3,
                    }}
                  >
                    {allImages.map((item, index) => (
                      <Box key={`image-${index}`} sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden",
                            padding: 1,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                            "&:hover": {
                              boxShadow: 2,
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => {
                            setSelectedImage(item);
                            setImagePopupOpen(true);
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image_url}
                            alt={`image-${index}`}
                            sx={{
                              width: "100%",
                              height: "170px",
                              objectFit: "contain",
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <IconButton
                          color="secondary"
                          disabled={
                            Number(
                              projectPackagingOpsStore.packagingOpsSwagpackMedia
                                ?.archived,
                            ) === 1
                          }
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                            backgroundColor:
                              theme.palette.customColors.pinkLight,
                            width: 30,
                            height: 30,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMedia(item.id, true);
                          }}
                        >
                          <DeleteIcon
                            sx={{
                              color: theme.palette.customColors.pinkDark,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    {" "}
                    <CustomTypography
                      variant="heading6"
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                      }}
                    >
                      NO IMAGE FOUND
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === "video" && (
              <Box>
                {allVideos.length > 0 ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {allVideos.map((item, index) => (
                      <Box key={`video-${index}`} sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            position: "relative",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            padding: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                            "&:hover": {
                              boxShadow: 2,
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => {
                            if (item.video_url) {
                              setSelectedVideo(item.video_url);
                              setVideoPopupOpen(true);
                            }
                          }}
                        >
                          <Box
                            component="video"
                            src={item.video_url}
                            muted
                            sx={{
                              width: "100%",
                              height: "165px",
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                          <Box
                            component="img"
                            src={playBtn}
                            alt="Play Video"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              width: "60px",
                              height: "60px",
                            }}
                          />
                        </Box>
                        <IconButton
                          color="secondary"
                          disabled={
                            Number(
                              projectPackagingOpsStore.packagingOpsSwagpackMedia
                                ?.archived,
                            ) === 1
                          }
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                            backgroundColor:
                              theme.palette.customColors.pinkLight,
                            width: 30,
                            height: 30,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMedia(item.id, false);
                          }}
                        >
                          <DeleteIcon
                            sx={{
                              color: theme.palette.customColors.pinkDark,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    {" "}
                    <CustomTypography
                      variant="heading6"
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                      }}
                    >
                      NO VIDEOS FOUND
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Dialog
          open={videoPopupOpen}
          onClose={() => setVideoPopupOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              overflow: "visible",
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(8px)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 600,
              height: 350,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              backgroundColor: theme.palette.customColors.white,
              borderRadius: 2,
              p: 1,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                overflow: "hidden",
                p: 1,
                backgroundColor: theme.palette.customColors.white,
              }}
            >
              {selectedVideo && (
                <video
                  src={selectedVideo}
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>

            <IconButton
              sx={{
                position: "fixed",
                top: 15,
                backgroundColor: theme.palette.customColors.blackBorder,
                right: 25,
                color: theme.palette.customColors.white,
                backdropFilter: "blur(2px)",
                zIndex: 20,
                width: "36px",
                height: "36px",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
              }}
              aria-label="close"
              onClick={() => {
                setVideoPopupOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Dialog>

        <ImageGalleryPopup
          key={"productImages"}
          open={imagePopupOpen}
          onClose={() => setImagePopupOpen(false)}
          images={
            selectedImage?.image_url
              ? [
                  {
                    id: selectedImage.id,
                    src: selectedImage.image_url,
                  },
                ]
              : []
          }
          title={"Image"}
        />
      </Container>
    </>
  );
};

export default observer(PackagingOpsMedia);
